import { Dispatch, useMemo, useCallback } from 'react';
// codeSearch is a shared action - results are stored in codeBench state
import { codeSearch } from '../actions/code-bench.actions';
import { addCode } from '../actions/grade-bench-panel-actions';
import * as commonEnums from '../enums';

export interface UseCodeSearchParams {
  dispatch: Dispatch<any>;
  caseId?: string; // Optional - can be provided via context instead
  lookupKey?: string | number; // Optional lookup key for search results (transaction ID, span ID, invoice ID, etc.)
  activeItem?: CMxAPI.CaseRun;
  userContext?: CMxAPI.OrganizationContext;
  processInstance?: AmplifyAPI.ProcessInstance;
  searchResultsMap?: {
    v2: Record<string, any>;
    v1: Record<string, CMxCommonApp.CodeInfo[]>;
  };
}

export interface CodeSearchContext {
  transaction?: Accelerate.CQATransaction | CMxCommonApp.CodableTransaction;
  invoice?: Accelerate.CQAInvoice | CMxCommonApp.CodableInvoice;
  processInstance?: AmplifyAPI.ProcessInstance;
  activeItem?: CMxAPI.CaseRun;
  userContext?: CMxAPI.OrganizationContext;
  selectedSpan?: Accelerate.SpanAnnotation;
  documentId?: string | number;
  caseId?: string;
  spans?: Accelerate.SpanAnnotation[];
}

/**
 * Code search hook for grade-bench-panel and annotation components.
 * Can be used by:
 * - Annotation wrappers (with span context)
 * - Grade bench panel generalized (with transaction context)
 * 
 * Note: activeItem, userContext, processInstance, and searchResultsMap should be
 * obtained via useSelector in the calling component and passed in as parameters.
 */
export const useCodeSearch = ({
  dispatch,
  caseId,
  lookupKey,
  activeItem,
  userContext,
  processInstance,
  searchResultsMap = { v2: {}, v1: {} }
}: UseCodeSearchParams) => {

  // Compute search results for the current lookup key
  const searchResults = useMemo(() => {
    const lookup = lookupKey?.toString() || '';
    if (!lookup) return [];
    
    // Try codeSearchResultsV2 first (structured by code type), fallback to searchResults
    const v2Results = searchResultsMap.v2[lookup];
    const v1Results = searchResultsMap.v1[lookup] || [];
    
    // If v2 exists, flatten it (it's structured by code type), otherwise return v1
    if (v2Results && typeof v2Results === 'object' && !Array.isArray(v2Results)) {
      return [
        ...(v2Results['CPT'] || []),
        ...(v2Results['ICD'] || []),
        ...(v2Results['MODIFIER'] || [])
      ];
    }
    
    return Array.isArray(v2Results) ? v2Results : v1Results;
  }, [searchResultsMap, lookupKey]);

  /**
   * Dispatch code search action
   * @param codeQuery - The code query with text, searchType, etc.
   * @param context - Additional context (transaction, span, invoice, etc.)
   * @param searchResultsLookupKey - Optional override for lookup key
   */
  const onCodeSearch = useCallback((
    codeQuery: CMxCommonApp.CodeQuery,
    context?: CodeSearchContext,
    searchResultsLookupKey?: string | number
  ) => {
    const effectiveLookupKey = searchResultsLookupKey || lookupKey;
    
    // Build full context with required fields
    // activeItem and userContext are required for codeSearch action (derived from Redux state)
    const fullContext: any = {
      ...codeQuery.context,
      activeItem: context?.activeItem || activeItem,
      userContext: context?.userContext || userContext,
      transaction: context?.transaction,
      invoice: context?.invoice,
      processInstance: context?.processInstance || processInstance,
      selectedSpan: context?.selectedSpan
    };

    const codeQueryWithContext: Accelerate.CodeQuery = {
      ...codeQuery,
      context: fullContext,
      searchResultsLookupKey: effectiveLookupKey?.toString()
    };

    dispatch(codeSearch(codeQueryWithContext));
  }, [dispatch, lookupKey, activeItem, userContext, processInstance]);

  /**
   * Handle code selection from search results
   * Uses addCode from grade-bench-panel-actions which supports spans for annotations
   * 
   * @param codeAction - The selected code action payload
   * @param context - Additional context (transaction, span, invoice, etc.)
   */
  const onCodeSearchSelection = useCallback((
    codeAction: Accelerate.CodeActionPayload,
    context?: CodeSearchContext
  ) => {
    // Build the payload for grade-bench-panel addCode action
    // This supports spans for annotations
    const payload: Accelerate.CodeAddActionPayload = {
      codeInfo: codeAction.codeInfo,
      context: {
        ...codeAction.context,
        transaction: context?.transaction || codeAction.context?.transaction,
        invoice: context?.invoice || codeAction.context?.invoice,
        processInstance: context?.processInstance || processInstance || codeAction.context?.processInstance,
        activeItem: context?.activeItem || activeItem || codeAction.context?.activeItem
      },
      documentId: context?.documentId,
      caseId: context?.caseId || caseId,
      spans: context?.spans
    };

    // Use grade-bench-panel addCode which supports spans
    dispatch(addCode(payload));
  }, [dispatch, caseId, activeItem, processInstance]);

  /**
   * Get search results for a specific lookup key
   * @param key - The lookup key (transaction ID, span ID, etc.)
   * @param codeType - Optional code type filter for v2 results
   */
  const getSearchResults = useMemo(() => {
    return (key?: string | number, codeType?: CMxCommonApp.ChargeSessionCodeType): CMxCommonApp.CodeInfo[] => {
      const lookup = key?.toString() || lookupKey?.toString() || '';
      
      if (!lookup) return [];
      
      const v2Results = searchResultsMap.v2[lookup];
      const v1Results = searchResultsMap.v1[lookup] || [];

      console.log('in getSearchResults lookup', lookup, 'v2Results', v2Results, 'v1Results', v1Results);
      
      if (v2Results && typeof v2Results === 'object' && !Array.isArray(v2Results)) {
        if (codeType) {
          return v2Results[codeType] || [];
        }
        // Flatten all code types
        console.log('in getting v2 flattend');
        return [
          ...(v2Results['CPT'] || []),
          ...(v2Results['MODIFIER'] || []),
          ...(v2Results['ICD'] || [])
        ];
      }
      
      console.log('in getting v2 not flattend');
      console.log('v2Results', v2Results, 'v1Results', v1Results);
      return Array.isArray(v2Results) ? v2Results : v1Results;
    };
  }, [searchResultsMap, lookupKey]);

  return {
    onCodeSearch,
    onCodeSearchSelection,
    getSearchResults,
    searchResults
  };
};

